import { Visual } from '@/modules/Visual/Index';
import { Checkbox } from '@/modules/Checkbox';
import { ListItemCheckboxSelectionTone, ListItemProps, ListProps } from '@/shared/types/list';
import { cn } from '@/shared/utils/cn';
import { cva } from 'class-variance-authority';
import React from 'react';

export const ListSeparator = ({ className }: { className?: string }) => {
  return (
    <div
      className={cn(
        'ui-my-1 ui-box-border ui-h-0 ui-w-full ui-border-0 ui-border-t ui-border-solid ui-border-default',
        className
      )}
      role="presentation"
    />
  );
};

export const ListItem = ({
  children,
  icon,
  onClick,
  disabled = false,
  current = false,
  uppercase = false,
  color = 'blue',
  'data-option-value': dataOptionValue,
  iconColor,
}: ListItemProps) => {
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) return;
    onClick?.(e);
  };

  const variant = cva(
    'ui-group ui-flex ui-items-start ui-gap-x-8 ui-transition-all duration-300 ui-px-8 ui-py-6 ui-rounded-control-sm ui-flex ui-items-center',
    {
      variants: {
        current: {
          blue: 'ui-bg-fill-brand ui-text-on-color',
          red: 'ui-bg-surface-danger ui-text-danger',
        },
        color: {
          blue: '',
          red: 'ui-bg-surface-danger ui-text-danger',
        },
        disabled: {
          true: 'ui-opacity-70 ui-cursor-not-allowed ui-select-none',
          false: 'hover:ui-cursor-pointer',
        },
        isCurrentActive: {
          true: '',
          false: '',
        },
      },
      compoundVariants: [
        // Default text color when NOT current (isCurrentActive = false)
        { isCurrentActive: false, color: 'blue', className: 'ui-text-primary' },
        { isCurrentActive: false, color: 'red', className: 'ui-text-danger' },

        // Hover states: gris claro para hover (cuando no está seleccionado)
        {
          isCurrentActive: false,
          color: 'blue',
          disabled: false,
          className: 'hover:ui-bg-fill-hover',
        },
        {
          isCurrentActive: false,
          color: 'red',
          disabled: false,
          className: 'hover:ui-bg-surface-danger-hover hover:ui-text-danger',
        },

        // Current/selected states: mantener azul oscuro
        {
          current: 'blue',
          disabled: false,
          className: 'ui-bg-fill-brand ui-text-on-color',
        },
        {
          current: 'red',
          disabled: false,
          className: 'hover:ui-bg-surface-danger hover:ui-text-danger',
        },
      ],
    }
  );

  const isCurrentActive = typeof current === 'boolean' ? current : !!current;

  return (
    <li
      className={`${variant({
        current: isCurrentActive ? (typeof current === 'boolean' ? color : current) : undefined,
        color,
        disabled,
        isCurrentActive,
      })} ${uppercase ? 'ui-uppercase' : ''} ui-w-full`}
      onClick={handleClick}
      data-option-value={dataOptionValue}
    >
      {icon && (
        <div className="ui-flex-shrink-0">
          <Visual
            slot={icon}
            colorIcon={iconColor || (current ? 'white' : 'current')}
            size="sm"
            defaultSizeIcon={false}
            fallback={children?.toString().substring(0, 1)}
          />
        </div>
      )}
      <span className="ui-word-break-break-word ui-min-w-0 ui-flex-1 ui-whitespace-normal ui-break-words ui-text-body-sm-medium ui-font-body-sm-medium ui-leading-body-sm-medium ui-tracking-body-sm">
        {children}
      </span>
    </li>
  );
};

// Nuevo ListItem con checkbox que solo permite hacer clic en el checkbox
export const ListItemWithCheckbox = ({
  children,
  icon,
  disabled = false,
  current = false,
  uppercase = false,
  color = 'blue',
  'data-option-value': dataOptionValue,
  iconColor,
  checkboxProps,
  isGroupTitle = false,
  hasCurrent = true,
  groupTitle,
  checkboxSelectionTone = 'brand' as ListItemCheckboxSelectionTone,
  checkboxPosition = 'start',
  compact = false,
  avatarUrl,
}: ListItemProps & {
  checkboxProps: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  };
}) => {
  const variant = cva(
    'ui-group ui-flex ui-items-center ui-gap-8 ui-text-body-sm-regular ui-transition-all duration-300 ui-rounded-control-sm ui-w-full ui-leading-none',
    {
      variants: {
        padding: {
          default: 'ui-px-8 ui-py-6',
          compact: 'ui-min-h-32 ui-px-8 ui-py-4',
        },
        selectionTone: {
          brand: '',
          subtle: '',
        },
        current: {
          inactive: '',
          blue: '',
          red: '',
        },
        color: {
          blue: 'ui-text-primary',
          red: 'ui-bg-surface-danger ui-text-on-color',
        },
        disabled: {
          true: 'ui-opacity-70 ui-cursor-not-allowed ui-select-none',
          false: 'ui-cursor-default',
        },
      },
      compoundVariants: [
        {
          selectionTone: 'brand',
          current: 'blue',
          disabled: false,
          className: 'ui-bg-fill-brand ui-text-on-color',
        },
        {
          selectionTone: 'brand',
          current: 'red',
          disabled: false,
          className: 'ui-bg-surface-danger ui-text-on-color',
        },
        {
          selectionTone: 'subtle',
          current: 'blue',
          disabled: false,
          className: 'ui-bg-fill-hover ui-text-primary hover:ui-bg-fill-hover',
        },
        {
          selectionTone: 'subtle',
          current: 'red',
          disabled: false,
          className: 'ui-bg-fill-hover ui-text-primary hover:ui-bg-fill-hover',
        },
        {
          selectionTone: 'brand',
          current: 'inactive',
          disabled: false,
          className: 'hover:ui-bg-fill-hover',
        },
        {
          selectionTone: 'subtle',
          current: 'inactive',
          disabled: false,
          className: 'hover:ui-bg-fill-hover',
        },
      ],
    }
  );

  // Prevenir la propagación del clic en el checkbox para que no se active el onClick del ListItem
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const resolvedCurrent = hasCurrent
    ? typeof current === 'boolean'
      ? current
        ? 'blue'
        : undefined
      : current
    : undefined;

  const currentKey: 'inactive' | 'blue' | 'red' =
    resolvedCurrent === 'blue' ? 'blue' : resolvedCurrent === 'red' ? 'red' : 'inactive';

  const checkboxNode = (
    <div onClick={handleCheckboxClick} className="ui-flex ui-flex-shrink-0 ui-cursor-pointer">
      <Checkbox
        checked={checkboxProps.checked}
        onChange={(val) => checkboxProps.onChange(val)}
        disabled={checkboxProps.disabled || disabled}
        size="md"
      />
    </div>
  );

  const labelNode = (
    <span className="ui-word-break-break-word ui-min-w-0 ui-flex-1 ui-whitespace-normal ui-break-words ui-text-body-sm-regular ui-font-body-sm-medium ui-tracking-body-sm">
      {children}
    </span>
  );

  const mediaNode =
    avatarUrl || icon ? (
      <div className="ui-flex ui-flex-shrink-0 ui-items-center">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="ui-h-16 ui-w-16 ui-rounded-control-pill ui-object-cover"
          />
        ) : (
          <Visual
            slot={icon}
            colorIcon={
              iconColor ||
              (checkboxSelectionTone === 'brand' && resolvedCurrent === 'blue'
                ? 'white'
                : 'current')
            }
            size="sm"
            defaultSizeIcon={false}
            fallback={children?.toString().substring(0, 1)}
          />
        )}
      </div>
    ) : null;

  if (isGroupTitle) {
    return (
      <li
        className={cn(
          'ui-font-weight-600 ui-rounded-control-sm ui-px-8 ui-leading-none ui-text-[#898989]',
          compact ? 'ui-min-h-32 ui-py-4' : 'ui-py-6',
          uppercase && 'ui-uppercase'
        )}
        data-option-value={dataOptionValue}
      >
        {groupTitle || children}
      </li>
    );
  }

  if (checkboxPosition === 'end') {
    return (
      <li
        className={`${variant({
          current: currentKey,
          color,
          disabled,
          padding: compact ? 'compact' : 'default',
          selectionTone: checkboxSelectionTone,
        })} ${uppercase ? 'ui-uppercase' : ''}`}
        data-option-value={dataOptionValue}
      >
        {mediaNode}
        {labelNode}
        {checkboxNode}
      </li>
    );
  }

  return (
    <li
      className={`${variant({
        current: currentKey,
        color,
        disabled,
        padding: compact ? 'compact' : 'default',
        selectionTone: checkboxSelectionTone,
      })} ${uppercase ? 'ui-uppercase' : ''}`}
      data-option-value={dataOptionValue}
    >
      {checkboxNode}
      {mediaNode}
      {labelNode}
    </li>
  );
};

export const List = ({
  items = [],
  icon,
  onClick,
  titleName = 'label',
  valueName = 'id',
  currentValue,
  className,
  children,
  withCheckbox = false,
  hasCurrent = true,
  onCheckboxChange,
  padding = 'none',
}: ListProps & {
  withCheckbox?: boolean;
  onCheckboxChange?: (itemId: string | number, checked: boolean) => void;
}) => {
  const paddingClass = {
    none: 'ui-p-0',
    sm: 'ui-p-8',
    md: 'ui-p-12',
    lg: 'ui-p-16',
  }[padding];

  if (items.length === 0 && !children) return null;

  return (
    <ul
      className={cn('ui-m-0 ui-flex ui-w-full ui-list-none ui-flex-col', paddingClass, className)}
    >
      {children
        ? children
        : items.map((item) => {
            if (withCheckbox) {
              return (
                <ListItemWithCheckbox
                  key={item.id}
                  hasCurrent={hasCurrent}
                  icon={item.icon || icon}
                  disabled={item.disabled}
                  current={currentValue === item[valueName as keyof typeof item]}
                  checkboxProps={{
                    checked: item.checked || false,
                    onChange: (checked) => {
                      const itemValue = item[valueName as keyof typeof item];
                      if (
                        itemValue !== undefined &&
                        (typeof itemValue === 'string' || typeof itemValue === 'number')
                      ) {
                        onCheckboxChange?.(itemValue, checked);
                      }
                    },
                    disabled: item.disabled,
                  }}
                >
                  {titleName && String(item[titleName as keyof typeof item] ?? '')}
                </ListItemWithCheckbox>
              );
            }

            return (
              <ListItem
                key={item.id}
                icon={item.icon || icon}
                onClick={(e) => onClick?.(item, e)}
                disabled={item.disabled}
                current={currentValue === item[valueName as keyof typeof item]}
                color={item.color}
                iconColor={item.iconColor}
              >
                {titleName && String(item[titleName as keyof typeof item] ?? '')}
              </ListItem>
            );
          })}
    </ul>
  );
};

/*
Ejemplo de uso del ListItemWithCheckbox:

const itemsWithCheckbox = [
  { id: '1', label: 'Tarea 1', checked: false },
  { id: '2', label: 'Tarea 2', checked: true },
  { id: '3', label: 'Tarea 3', checked: false, disabled: true },
]

const handleCheckboxChange = (itemId: string, checked: boolean) => {
  console.log(`Item ${itemId} cambió a ${checked}`)
}

<List 
  items={itemsWithCheckbox}
  withCheckbox={true}
  onCheckboxChange={handleCheckboxChange}
  titleName="label"
  valueName="id"
/>

O usar directamente el ListItemWithCheckbox:

<ListItemWithCheckbox
  checkboxProps={{
    checked: false,
    onChange: (checked) => console.log('Checkbox cambió a:', checked),
    disabled: false
  }}
>
  Mi elemento con checkbox
</ListItemWithCheckbox>
*/
